import React from "react";
// Energy Audit Report

// Default
import { useState } from "react";

// Mui
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid2";
import Divider from "@mui/material/Divider";

// Custom
import Layout from "../../layout";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import DashboardCard from "../card/DashboardCard";
import EnergyAuditReportLineChart from "../charts/EnergyAuditReportLineChart";
import EnergyAuditReportDonutChart from "../charts/EnergyAuditReportDonutChart";

// Svg
import Calendar2 from "../../assets/svg/calendar2";
import Download from "../../assets/svg/download";

const EnergyAuditReport: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const str = ""; // > North > CBD;

  const firstOptions = [
    { value: "North", label: "North Circle" },
    { value: "South", label: "South Circle" },
    { value: "East", label: "East Circle" },
  ];

  const secondOptions = [
    { value: "CBD", label: "CBD Division" },
    { value: "Industrial", label: "Industrial Division" },
    { value: "Residential", label: "Residential Division" },
  ];

  const thirdOptions = [
    { value: "Commercial", label: "Commercial Zone" },
    { value: "Residential", label: "Residential Zone" },
  ];

  const fourthOptions = [
    { value: "All", label: "All Feeders" },
    { value: "F1", label: "Feeder 01" },
    { value: "F2", label: "Feeder 02" },
  ];

  const analysisData = [
    {
      title: "Feeder Loss Analysis",
      avg: "Daily Average",
      percentage: "5.4%",
      head: "Feeder Head: 5,630 kWh",
      sum: "DT Sum: 5,326 kWh",
    },
    {
      title: "LT Energy Loss",
      avg: "Daily Average",
      percentage: "4.2%",
      head: "DT Reading: 5,630 kWh",
      sum: "Consumer Sum: 5,326 kWh",
    },
    {
      title: "Total System Loss",
      avg: "Daily Average",
      percentage: "9.6%",
      head: "Technical: 6.8%",
      sum: "Commercial: 2.8%",
    },
  ];

  const rankingsData = [
    {
      title: "Feeder Performance Rankings",
      topText: "Top Performing Feeders",
      topTitle: "Feeder 05",
      topLossOrProfit: "2.82% loss",
      bottomText: "Bottom Performing Feeders",
      bottomTitle: "Feeder 04",
      bottomLossOrProfit: "9.18% loss",
    },
    {
      title: "DT Performance Rankings",
      topText: "Top Performing DTs",
      topTitle: "DT-103",
      topLossOrProfit: "2.33% loss",
      bottomText: "Bottom Performing DTs",
      bottomTitle: "DT-102",
      bottomLossOrProfit: "8.22% loss",
    },
  ];

  const analysisCard = ({ title, avg, percentage, head, sum }: any) => {
    return (
      <Card sx={{ padding: "12px 14px" }}>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}>
          <Typography variant="body1">{title}</Typography>
          <Typography variant="caption" color="#7D808F">
            {avg}
          </Typography>
        </Stack>

        <Typography variant="h5" fontSize={"22px"} mt={"20px"}>
          {percentage}
        </Typography>

        <Typography variant="body2" mt={"15px"} color="#7D808F">
          {head}
        </Typography>
        <Typography variant="body2" color="#7D808F">
          {sum}
        </Typography>
      </Card>
    );
  };

  const rankingsCard = ({
    title,
    topText,
    topTitle,
    topLossOrProfit,
    bottomText,
    bottomTitle,
    bottomLossOrProfit,
  }: any) => {
    return (
      <Card>
        <CardHeader title={<Typography variant="body1">{title}</Typography>} />
        <Divider />

        <CardContent>
          <Stack
            flex={2}
            flexDirection={"row"}
            gap={4}
            divider={<Divider orientation="vertical" flexItem />}>
            <Stack flex={1}>
              <Typography variant="caption" color="#54990E">
                {topText}
              </Typography>
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                mt={"7px"}
                mb={"0px"}>
                <Typography variant="body1">{topTitle}</Typography>
                <Typography variant="body1">{topLossOrProfit}</Typography>
              </Stack>
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                mt={"7px"}
                mb={"10px"}>
                <Typography variant="body1">{topTitle}</Typography>
                <Typography variant="body1">{topLossOrProfit}</Typography>
              </Stack>
            </Stack>

            <Stack flex={1}>
              <Typography variant="caption" color="#FF3B30">
                {bottomText}
              </Typography>
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                mt={"7px"}>
                <Typography variant="body1">{bottomTitle}</Typography>
                <Typography variant="body1">{bottomLossOrProfit}</Typography>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      <Card sx={{ padding: "12px 14px" }}>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}>
          <Typography variant="body1">
            Energy Audit/Loss Report &nbsp; &nbsp;
            <Typography fontSize={"12px"} color="#7D808F">
              {str}
            </Typography>
          </Typography>

          <Stack direction={"row"} gap={"20px"} alignItems={"center"}>
            <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
              <Calendar2 />
            </IconButton>
            <Typography variant="caption">
              Last Updated: 10/24/2024, 4:46:11 PM
            </Typography>
            <Download />
          </Stack>
        </Stack>

        <Grid container spacing={2.25} mt={"20px"}>
          <Grid size={{ xs: 12, md: 3, lg: 3 }}>
            <CustomOutlinedSelect
              placeholder={"Select Circle"}
              options={firstOptions}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3, lg: 3 }}>
            <CustomOutlinedSelect
              placeholder={"Select Division"}
              options={secondOptions}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3, lg: 3 }}>
            <CustomOutlinedSelect
              placeholder={"Select Zone"}
              options={thirdOptions}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3, lg: 3 }}>
            <CustomOutlinedSelect
              placeholder={"Select Feeder"}
              options={fourthOptions}
            />
          </Grid>
        </Grid>
      </Card>

      <Grid container spacing={2.25} mt={"20px"}>
        {analysisData.map((item, index) => {
          return (
            <Grid size={{ xs: 12, md: 4, lg: 4 }} key={index}>
              {analysisCard(item)}
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={2.25} mt={"20px"}>
        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
          <DashboardCard
            title={"Feeder Loss Report (Daily)"}
            showIcons={false}
            sx={{ padding: "12px 14px", height: "300px" }}
            body={<EnergyAuditReportLineChart />}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
          <DashboardCard
            title={"LT Energy Loss Report"}
            showIcons={false}
            sx={{ padding: "12px 14px", height: "300px" }}
            body={<EnergyAuditReportDonutChart />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.25} my={"20px"}>
        {rankingsData.map((item, index) => {
          return (
            <Grid size={{ xs: 12, md: 6, lg: 6 }} key={index}>
              {rankingsCard(item)}
            </Grid>
          );
        })}
      </Grid>
    </Layout>
  );
};

export default EnergyAuditReport;
